import React, { useState, useEffect } from 'react';

const Banner = ({ 
  banners,
  language = 'en' // 'en' or 'km'
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // Translations
  const translations = {
    en: {
      welcome: 'Welcome to Our Store',
      discover: 'Discover amazing products curated just for you.',
      imageNotFound: 'Image Not Found',
      previous: 'Previous banner',
      next: 'Next banner'
    },
    km: {
      welcome: 'សូមស្វាគមន៍មកកាន់ហាងរបស់យើង',
      discover: 'ស្វែងយល់ពីផលិតផលដ៏អស្ចារ្យដែលបានជ្រើសរើសសម្រាប់អ្នក',
      imageNotFound: 'រកមិនឃើញរូបភាព',
      previous: 'បដាមុន',
      next: 'បដាបន្ទាប់'
    }
  };

  const t = translations[language] || translations.en;

  // Get font family based on language
  const getFontFamily = () => {
    if (language === 'km') {
      return "'Kantumruy Pro', 'Noto Sans Khmer', 'Noto Serif Khmer', 'Khmer OS', sans-serif";
    }
    return "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
  };

  useEffect(() => {
    if (banners && banners.length > 1 && !isHovering) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % banners.length);
      }, 5000);
      return () => clearInterval(timer);
    }
  }, [banners, isHovering]);

  // Get image URL with safety check
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return 'https://via.placeholder.com/1920x600?text=No+Image';
    if (imageUrl.startsWith('http')) return imageUrl;
    if (imageUrl.startsWith('data:')) return imageUrl;
    return `https://easymenu-backend-4w2d.onrender.com${imageUrl}`;
  };

  // Safety check - if no banners, show default
  if (!banners || banners.length === 0) {
    return (
      <div 
        className="relative w-full flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden py-16 md:py-24"
        style={{ fontFamily: getFontFamily() }}
      >
        <div className="text-center px-6 z-10">
          <div className="w-20 h-20 rounded-full bg-gray-200/50 flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-store text-3xl text-gray-400"></i>
          </div>
          <h1 
            className="text-2xl md:text-4xl lg:text-5xl font-light text-gray-800 mb-3 tracking-tight"
            style={{ fontFamily: getFontFamily() }}
          >
            {t.welcome}
          </h1>
          <p 
            className="text-gray-500 text-base md:text-lg max-w-2xl mx-auto"
            style={{ fontFamily: getFontFamily() }}
          >
            {t.discover}
          </p>
        </div>
        {/* Subtle decorative elements */}
        <div className="absolute -top-20 -right-20 w-64 h-64 bg-gray-200/30 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-gray-200/30 rounded-full blur-3xl"></div>
      </div>
    );
  }

  // Get current banner safely
  const currentBanner = banners[currentIndex] || banners[0] || {};

  return (
    <div 
      className="relative w-full bg-gray-50 overflow-hidden group rounded-2xl shadow-sm"
      style={{ fontFamily: getFontFamily() }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="relative w-full">
        {/* Main Image - Full width, auto height */}
        <img
          src={getImageUrl(currentBanner.image_url)}
          alt={currentBanner.title || t.welcome}
          className="w-full h-auto object-cover max-h-[400px] md:max-h-[500px] lg:max-h-[600px]"
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = 'https://via.placeholder.com/1920x600?text=Image+Not+Found';
          }}
        />

        {/* Gradient Overlay for Better Text Readability */}
        <div className="absolute inset-0 "></div>

        {/* Text Overlay */}
        {currentBanner.title && (
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8 text-white z-20">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 
                className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-2 tracking-tight drop-shadow-lg"
                style={{ fontFamily: getFontFamily() }}
              >
                {currentBanner.title}
              </h2>
              {currentBanner.subtitle && (
                <p 
                  className="text-sm md:text-base text-white/90 max-w-2xl drop-shadow-md"
                  style={{ fontFamily: getFontFamily() }}
                >
                  {currentBanner.subtitle}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows - Only show if more than 1 banner */}
      {banners.length > 1 && (
        <>
          <button
            onClick={() => setCurrentIndex((prev) => (prev - 1 + banners.length) % banners.length)}
            className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-30 p-2 md:p-3 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label={t.previous}
          >
            <i className="fas fa-chevron-left text-xs md:text-sm"></i>
          </button>
          
          <button
            onClick={() => setCurrentIndex((prev) => (prev + 1) % banners.length)}
            className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-30 p-2 md:p-3 rounded-full bg-white/90 backdrop-blur-sm text-gray-700 shadow-md opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:scale-110 focus:outline-none focus:ring-2 focus:ring-gray-400"
            aria-label={t.next}
          >
            <i className="fas fa-chevron-right text-xs md:text-sm"></i>
          </button>
          
          {/* Progress Indicators */}
          <div className="absolute bottom-4 md:bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`transition-all duration-300 rounded-full ${
                  index === currentIndex 
                    ? 'w-8 md:w-10 h-1.5 bg-white shadow-md' 
                    : 'w-1.5 md:w-2 h-1.5 md:h-2 bg-white/60 hover:bg-white/80 hover:w-2 md:hover:w-3'
                }`}
                aria-label={`Go to banner ${index + 1}`}
              />
            ))}
          </div>
        </>
      )}

      {/* Banner Counter - Optional */}
      {banners.length > 1 && (
        <div className="absolute top-4 right-4 z-30 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full">
          {currentIndex + 1} / {banners.length}
        </div>
      )}
    </div>
  );
};

export default Banner;